/*
 * i18n-Konvention
 * - Wörterbücher liegen pro Namensraum in eigenen Dateien: locales/<sprache>/<namensraum>.js
 *   (z. B. locales/de/common.js, locales/en/common.js) und exportieren ein flaches Objekt.
 * - Jede Sprache führt ihre Namensräume in locales/<sprache>/index.js zusammen.
 *   Neue Namensräume = neue Datei + je eine Import-/Spread-Zeile in der Sprach-Index-Datei.
 * - Schlüssel tragen den Namensraum als Präfix (z. B. 'nav.dashboard'), damit sie nicht kollidieren.
 * - Fehlende Schlüssel fallen auf Deutsch zurück, danach auf den Schlüssel selbst.
 * - Texte mit Platzhaltern: t('login.greeting', { name }) für "Hallo, {name}!".
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import de from './locales/de';
import en from './locales/en';
import { interpolate, localeFor } from './utils';

export const DEFAULT_LANG = 'de';
export const SUPPORTED_LANGS = ['de', 'en'];
const DICTIONARIES = { de, en };
const COOKIE_NAME = 'lang';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function readLangCookie() {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_NAME}=`));
  const value = match ? decodeURIComponent(match.slice(COOKIE_NAME.length + 1)) : null;
  return SUPPORTED_LANGS.includes(value) ? value : DEFAULT_LANG;
}

function writeLangCookie(lang) {
  document.cookie = `${COOKIE_NAME}=${lang}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readLangCookie);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGS.includes(next)) return;
    writeLangCookie(next);
    setLangState(next);
  }, []);

  const t = useCallback((key, params) => {
    const text = DICTIONARIES[lang][key] ?? DICTIONARIES[DEFAULT_LANG][key] ?? key;
    return interpolate(text, params);
  }, [lang]);

  const productName = t('app.name');

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = productName;
  }, [lang, productName]);

  const value = useMemo(
    () => ({ lang, setLang, t, locale: localeFor(lang), productName }),
    [lang, setLang, t, productName]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n muss innerhalb von I18nProvider verwendet werden');
  return ctx;
}

export const useT = () => useI18n().t;
